import { FC, useEffect, useMemo, useState } from "react";
import { Accordion, Badge, Spinner, Table } from "react-bootstrap";

import { AppUserApi } from "../../api";
import { ActivityTargetTraceType } from "../../DTO";
import { selectUser } from "../../redux-config/entities/user";
import { useAppSelector } from "../../redux-config/hooks";
import "./style.scss";

type ActivityTargetTraceProps = {
    userId: number;
    compact?: boolean;
    selectedWeekIndex?: number;
};

const formatSteps = (value: number) => new Intl.NumberFormat("en-US").format(value);

const actionLabel = (validDays: number, goalDays: number) => {
    if (validDays < 4) return "Insufficient data";
    if (goalDays >= 5) return "Progress";
    if (goalDays >= 3) return "Hold";
    return "Ease";
};

const actionVariant = (label: string) => {
    if (label === "Progress") return "success";
    if (label === "Hold") return "warning";
    if (label === "Ease") return "danger";
    return "secondary";
};

export const ActivityTargetTrace: FC<ActivityTargetTraceProps> = ({
    userId,
    compact = false,
    selectedWeekIndex,
}) => {
    const user = useAppSelector(selectUser);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [trace, setTrace] = useState<ActivityTargetTraceType>();

    useEffect(() => {
        if (!userId) return;

        setLoading(true);
        setError("");

        AppUserApi.ActivityTargetTrace(user, userId)
            .then((data) => setTrace(data.trace))
            .catch((err) => setError(String(err || "Activity target trace unavailable.")))
            .finally(() => setLoading(false));
    }, [user, userId]);

    const weeks = useMemo(() => {
        if (!trace) return [];
        if (!selectedWeekIndex) return trace.weeks;
        return trace.weeks.filter((week) => week.weekIndex === selectedWeekIndex);
    }, [selectedWeekIndex, trace]);

    if (loading) {
        return (
            <div className="activity-target-trace activity-target-trace-loading">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (error) {
        return <div className="activity-target-trace activity-target-trace-empty">{error}</div>;
    }

    if (!trace) return null;

    const target = trace.currentTarget;

    return (
        <div className="activity-target-trace">
            <div className="activity-target-trace-head">
                <div>
                    <div className="activity-target-trace-title">Activity Target Trace</div>
                    <div className="activity-target-trace-subtitle">
                        Goal day: minutes target or steps target
                    </div>
                </div>
                <Badge bg="danger">
                    {target.minutesPerDay} min or {formatSteps(target.stepsPerDay)} steps
                </Badge>
            </div>

            <div className="activity-target-trace-grid">
                <div className="activity-target-trace-kpi">
                    <span>Group</span>
                    <strong>{target.group || "-"}</strong>
                </div>
                <div className="activity-target-trace-kpi">
                    <span>Plan goal</span>
                    <strong>{target.planGoalId ?? "-"}</strong>
                </div>
                <div className="activity-target-trace-kpi">
                    <span>Baseline</span>
                    <strong>
                        {trace.baseline
                            ? `${trace.baseline.avgMinutes} min / ${formatSteps(trace.baseline.avgSteps)} steps`
                            : "Need 4 valid days"}
                    </strong>
                </div>
                <div className="activity-target-trace-kpi">
                    <span>Valid baseline days</span>
                    <strong>{trace.baseline?.validDays ?? 0}</strong>
                </div>
            </div>

            {!compact && (
                <>
                    <div className="table-responsive mt-3">
                        <Table hover className="align-middle activity-target-trace-table">
                            <thead>
                                <tr>
                                    <th>Week</th>
                                    <th>Valid days</th>
                                    <th>Goal days</th>
                                    <th>Total min</th>
                                    <th>Total steps</th>
                                    <th>Avg score</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {weeks.map((week) => {
                                    const label = actionLabel(week.validDays, week.goalDays);
                                    return (
                                        <tr key={week.weekId}>
                                            <td>Week {week.weekIndex}</td>
                                            <td>{week.validDays}</td>
                                            <td>{week.goalDays}</td>
                                            <td>{week.totalMinutes}</td>
                                            <td>{formatSteps(week.totalSteps)}</td>
                                            <td>{week.avgScore ?? "-"}</td>
                                            <td>
                                                <Badge bg={actionVariant(label)}>{label}</Badge>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {weeks.length > 0 && (
                        <Accordion className="mt-2">
                            {weeks.map((week) => (
                                <Accordion.Item eventKey={String(week.weekId)} key={week.weekId}>
                                    <Accordion.Header>Week {week.weekIndex} day drilldown</Accordion.Header>
                                    <Accordion.Body>
                                        <Table responsive hover size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Day ID</th>
                                                    <th>Minutes</th>
                                                    <th>Steps</th>
                                                    <th>Goal day</th>
                                                    <th>Score</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {week.days.map((day) => (
                                                    <tr key={day.dayId}>
                                                        <td>{day.dayId}</td>
                                                        <td>{day.minutes}</td>
                                                        <td>{formatSteps(day.steps)}</td>
                                                        <td>
                                                            <Badge bg={day.goalDay ? "success" : "secondary"}>
                                                                {day.goalDay ? "Yes" : "No"}
                                                            </Badge>
                                                        </td>
                                                        <td>{day.score ?? "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}

                    <div className="activity-target-trace-rules">
                        {Object.entries(trace.rules).map(([key, value]) => (
                            <div key={key}>
                                <strong>{key.replace(/_/g, " ")}:</strong> {value}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
